/**
 * Dashboard Page — data-fetching hook
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'

import { computeFinancialMetrics, type DashboardEntry, type FinancialMetrics } from './dashboard-helpers'
import { loadApiDashboardData, loadLocalDashboardData } from './useDashboard-helpers'
import { hasAnyFiscalPeriods, type FiscalPeriod } from '../../services/fiscal-period-service'
import { getLocalAccounts } from '../../services/local-db'
import { type TrialBalanceReport } from '../../services/reporting-service'
import { useLedgerContext } from '../../stores/ledger-store'
import { useOrganization } from '../../stores/organization-store'
import { useSettings } from '../../stores/settings-store'

// ============================================================================
// Types
// ============================================================================

export interface UseDashboardResult {
  isLoading: boolean
  recentEntries: DashboardEntry[]
  currentPeriod: FiscalPeriod | null
  trialBalance: TrialBalanceReport | null
  hasFiscalPeriods: boolean | null
  accountCount: number
  financialMetrics: FinancialMetrics
  currency: string
}

interface DashboardState {
  recentEntries: DashboardEntry[]
  currentPeriod: FiscalPeriod | null
  trialBalance: TrialBalanceReport | null
  hasFiscalPeriods: boolean | null
  accountCount: number
  setRecentEntries: (entries: DashboardEntry[]) => void
  setCurrentPeriod: (p: FiscalPeriod | null) => void
  setTrialBalance: (tb: TrialBalanceReport | null) => void
  setHasFiscalPeriods: (b: boolean | null) => void
  setAccountCount: (n: number) => void
}

function useDashboardState(): DashboardState {
  const [recentEntries, setRecentEntries] = useState<DashboardEntry[]>([])
  const [currentPeriod, setCurrentPeriod] = useState<FiscalPeriod | null>(null)
  const [trialBalance, setTrialBalance] = useState<TrialBalanceReport | null>(null)
  const [hasFiscalPeriods, setHasFiscalPeriods] = useState<boolean | null>(null)
  const [accountCount, setAccountCount] = useState<number>(0)
  return {
    recentEntries, currentPeriod, trialBalance, hasFiscalPeriods, accountCount,
    setRecentEntries, setCurrentPeriod, setTrialBalance, setHasFiscalPeriods, setAccountCount,
  }
}

async function fetchAccountCount(
  organizationId: string,
  currentLedgerId: string | null,
  setAccountCount: (n: number) => void,
): Promise<void> {
  try {
    const allAccounts = await getLocalAccounts(organizationId, currentLedgerId ?? undefined)
    setAccountCount(allAccounts.filter(a => !a.isHeader).length)
  } catch {
    // silently ignore
  }
}

interface SetterBundle {
  setRecentEntries: (entries: DashboardEntry[]) => void
  setCurrentPeriod: (p: FiscalPeriod | null) => void
  setTrialBalance: (tb: TrialBalanceReport | null) => void
  setHasFiscalPeriods: (b: boolean | null) => void
  setAccountCount: (n: number) => void
}

async function runDashboardLoad(
  organizationId: string, currentLedgerId: string | null, baseCurrency: string, setters: SetterBundle,
): Promise<void> {
  const hasPeriods = await hasAnyFiscalPeriods(organizationId)
  setters.setHasFiscalPeriods(hasPeriods)
  await fetchAccountCount(organizationId, currentLedgerId, setters.setAccountCount)
  const isApiSuccess = await loadApiDashboardData(organizationId, {
    setRecentEntries: setters.setRecentEntries,
    setCurrentPeriod: setters.setCurrentPeriod,
    setTrialBalance: setters.setTrialBalance,
  })
  if (!isApiSuccess) {
    try {
      await loadLocalDashboardData(organizationId, currentLedgerId, baseCurrency, {
        setRecentEntries: setters.setRecentEntries,
        setTrialBalance: setters.setTrialBalance,
        setCurrentPeriod: setters.setCurrentPeriod,
      })
    } catch {
      // Silently handle local-db errors
    }
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useDashboard(): UseDashboardResult {
  const { organizationId } = useOrganization()
  const { currentLedgerId, isLoaded: isLedgerLoaded } = useLedgerContext()
  const { settings } = useSettings()
  const [isLoading, setIsLoading] = useState(true)
  const state = useDashboardState()
  const loadingRef = useRef(false)
  const { setRecentEntries, setCurrentPeriod, setTrialBalance, setHasFiscalPeriods, setAccountCount } = state

  const loadDashboardData = useCallback(async (): Promise<void> => {
    if (loadingRef.current) { return }
    loadingRef.current = true
    setIsLoading(true)
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500))
    try {
      await runDashboardLoad(organizationId, currentLedgerId, settings.baseCurrency, {
        setRecentEntries, setCurrentPeriod, setTrialBalance, setHasFiscalPeriods, setAccountCount,
      })
    } finally {
      await minLoadingTime
      setIsLoading(false)
      loadingRef.current = false
    }
  }, [organizationId, currentLedgerId, settings.baseCurrency, setRecentEntries, setCurrentPeriod, setTrialBalance, setHasFiscalPeriods, setAccountCount])

  useEffect(() => {
    if (!isLedgerLoaded) { return }
    void loadDashboardData()
  }, [isLedgerLoaded, loadDashboardData])

  const financialMetrics = useMemo(() => computeFinancialMetrics(state.trialBalance), [state.trialBalance])

  return {
    isLoading,
    recentEntries: state.recentEntries,
    currentPeriod: state.currentPeriod,
    trialBalance: state.trialBalance,
    hasFiscalPeriods: state.hasFiscalPeriods,
    accountCount: state.accountCount,
    financialMetrics,
    currency: settings.baseCurrency,
  }
}
